import type { SessionTodoResponseData } from "@classes-service-types/index.ts";
import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRef, ref, toRaw, unref, watch } from "vue";
import programSessionsService from "@/services/programSessions";
import type { SessionTodoForm, UseSessionTodosReturn, WysiwygContent } from "../types";

const toSessionTodoForm = (todo: SessionTodoResponseData): SessionTodoForm => ({
  id: todo.id,
  todo_id: todo.todo_id,
  required: todo.required,
  placement: todo.placement,
  ranking: todo.ranking,
  display_name: todo.display_name ?? null,
  due_date: todo.due_date ?? null,
  instructions: (todo.instructions ?? {}) as WysiwygContent,
  supporting_url: todo.supporting_url ?? null,
});

/**
 * Fetches a session's to-dos and exposes them as an editable working copy.
 *
 * Persistence is intentionally NOT done here. The backend's session POST/PUT
 * (`programming/sessions/{id}?rules=True&housing=True`) treats `session_todos`
 * as a managed relationship: it archives rows whose `todo_id` is missing from
 * the payload and upserts the rest. So the orchestrator just drops the working
 * copy returned here onto the session save payload — no separate per-todo call
 * is needed (and adding one races with the backend's archive sweep).
 */
export const useSessionTodos = (
  sessionId: MaybeRef<string | undefined>,
  options: { enabled?: MaybeRef<boolean> } = {},
): UseSessionTodosReturn => {
  const unrefSessionId = computed(() => unref(sessionId));

  const enabled = computed(() => !!unrefSessionId.value && unref(options.enabled ?? true));

  const {
    data: sessionTodosData,
    isLoading: sessionTodosLoading,
    isFetched: sessionTodosFetched,
    error: sessionTodosError,
  } = useQuery({
    queryKey: ["sessionTodos", unrefSessionId],
    queryFn: async () => {
      const response = await programSessionsService.getProgramSessionTodos({
        session_id: unrefSessionId.value,
        limit: 60,
      });
      return response.data.items ?? [];
    },
    enabled,
  });

  const sessionTodos = ref<SessionTodoForm[]>([]);

  // Map from raw query data so subsequent reads of sessionTodos don't carry
  // Vue reactive proxies into anything that uses structuredClone downstream
  // (e.g. parent staging).
  watch(
    sessionTodosData,
    (newData) => {
      if (newData) {
        sessionTodos.value = toRaw(newData).map(toSessionTodoForm);
      }
    },
    { immediate: true },
  );

  // Whether the query has stopped waiting. A disabled query has nothing to wait for; an enabled
  // one is settled once it has resolved. `isFetched` (unlike `isLoading`) is already true for a
  // cache hit and for a failed fetch, so callers never wait on a transition that will not happen.
  const sessionTodosSettled = computed(() => !enabled.value || sessionTodosFetched.value);

  // Stricter: whether the working copy actually reflects the server. A failed fetch still counts
  // as settled, but leaves the copy empty (the hydration watch above skips on error), so anything
  // that writes session_todos back must check this instead — the backend archives rows missing
  // from the payload, and sending [] after a failed read would wipe them.
  const sessionTodosHydrated = computed(
    () => !enabled.value || (sessionTodosFetched.value && !sessionTodosError.value),
  );

  return {
    sessionTodos,
    sessionTodosLoading,
    sessionTodosSettled,
    sessionTodosHydrated,
    sessionTodosError,
  };
};
